import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';

import sha512 from 'js-sha512';

import PreSwapModal from 'shared/components/PreSwapModal';
import SwapMultipleModal from 'shared/components/SwapMultipleModal';

import ProductSearch from 'services/ProductSearch';
import analytics from 'services/analytics';
import modsyMixpanel from 'services/analytics/modsy_mixpanel';
import { userAddedProductFromPDP } from 'services/analytics/analytics';
import { SwapService } from 'services/LiveSwap';
import { bugsnagError } from 'services/bugsnag';

import { getCurrentSearchKey } from 'redux/StudioProductBrowser';
import {
  getActiveDesign,
  getActiveSpace,
  getHotspotForProduct,
  getSelectedHotspotProduct,
  getIsSharedSpace,
  getSelectedHotspotUid,
  getCurrentPackageData,
  getAllHotspotsForProduct,
  getCurrentSelectedHotspotUid,
  getCurrentSelectedHotspotProduct,
  getPDPHotspotProduct,
  getPDPHotspotUid,
  getCurrentPDPHotspotUid,
  getCurrentPDPHotspotProduct
} from 'redux/Studio';
import { getCurrentUserEmail, getCurrentUserId, userIsLoggedIn } from 'redux/User';
import { FEATURE_FLAGS, getFeatureFlagEnabledBool } from 'shared/reducers/featureFlag';
import { addProductToCart, getShoppingCart } from 'shared/components/Checkout/state';
import { USER_FACING_COPY } from 'shared/components/ProductDetailsPageV2/utils';
import { getIsPhone } from 'shared/selectors/device';

import {
  getAlternativeProduct,
  getCurrentQuantity,
  getCurrentQuantityMultiple,
  getSelectedProduct,
  updateProductQuantity
} from '../state';
import { getActiveRender } from 'redux/Studio/DesignViewer';

import {
  CRATE_AND_BARREL_PRODUCT_CODE,
  PARTNERS_PURCHASE_THROUGH_MODSY
} from 'utils/constants';

export class AddToCartActionsController extends Component {
  state = {
    cartIsLoading: false,
    showPreSwapModal: false,
    showSwapMultipleModal: false
  };

  componentDidUpdate (prevProps) {
    const { altProduct } = this.props;
    const { altProduct: prevPropsAltProduct } = prevProps;

    if (!altProduct || !prevPropsAltProduct) return;

    if (prevPropsAltProduct.id() !== altProduct.id()) {
      this.props.updateQuantity(1);
    }
  }

  componentWillUnmount () {
    this.props.updateQuantity(1);
  }

  /**
   * Handles the logic for product availability.
   * Note: Add-ons always come from Direct Vendors (DV).
   * That means we don't need the "Buy from retailer" logic.
   */
  updateCartButton () {
    const {
      altProduct,
      shoppingCart,
      selectedProduct,
      activeSpace,
      isOutOfStock,
      isBackOrdered
    } = this.props;

    const currentProduct = altProduct || selectedProduct;

    let buttonAction = () => {};
    let buttonClass = '';
    let buttonText = '';

    const isAlreadyInCart = shoppingCart && shoppingCart.isProductInCart(currentProduct);
    const isDiscontinued = currentProduct && currentProduct.isDiscontinued();
    const hasToBeBoughtFromRetailer = activeSpace && activeSpace.isPartnerSpace() &&
      !PARTNERS_PURCHASE_THROUGH_MODSY.includes(activeSpace.partnerSlug()) &&
      currentProduct.isFromActivePartner();

    switch (true) {
      case hasToBeBoughtFromRetailer:
        buttonAction = () => this.buyFromRetailer();
        buttonText = 'Buy from Retailer';
        break;
      case isAlreadyInCart:
        buttonClass = 'is-disabled';
        buttonText = USER_FACING_COPY['addedToCart'];
        break;
      case isOutOfStock:
        buttonClass = 'is-disabled';
        buttonText = isDiscontinued
          ? USER_FACING_COPY['discontinuedProduct']
          : USER_FACING_COPY['outOfStock'];
        break;
      case isBackOrdered:
        buttonClass = 'is-disabled';
        buttonText = USER_FACING_COPY['addToCart'];
        break;
      default:
        buttonAction = () => this.buyProduct();
        buttonText = USER_FACING_COPY['addToCart'];
    }

    return {
      buttonClass,
      buttonText,
      buttonAction
    };
  }

  /**
   *  Places product in user cart for purchase through Modsy.
   */
  async buyProduct () {
    const {
      activeDesign,
      activeSpace,
      altProduct,
      dispatchGetCurrentEnabledFiltersFor,
      dispatchGetCurrentPackageData,
      dispatchGetCurrentSearchKey,
      dispatchGetCurrentSearchResultFor,
      dispatchGetCurrentUserEmail,
      dispatchGetCurrentUserId,
      dispatchGetHotSpotForProduct,
      getCurrentSelectedHotspotProduct,
      selectedProduct,
      quantity,
      trackAddToCart,
      userIsLoggedIn,
      usingPDPHotspotData
    } = this.props;
    const product = altProduct || selectedProduct;

    // Start spinning loader.
    this.setState({ cartIsLoading: true });

    try {
      const designId = activeDesign && activeDesign.id();
      const productHotspotId = activeDesign && dispatchGetHotSpotForProduct({
        design: activeDesign,
        productId: selectedProduct.id()
      });

      const email = dispatchGetCurrentUserEmail();

      // Place product into cart.
      await this.props.addProductToCartCheckout({
        product,
        productHotspotId,
        quantity,
        designId,
        email
      });

      // Analytics.
      const userId = userIsLoggedIn && dispatchGetCurrentUserId();
      const selectedHotspotProduct = getCurrentSelectedHotspotProduct({ usingPDPHotspotData });
      const searchKey = dispatchGetCurrentSearchKey();
      const searchProducts = dispatchGetCurrentSearchResultFor(searchKey);
      const enabledFilters = dispatchGetCurrentEnabledFiltersFor(searchKey);
      const packageData = dispatchGetCurrentPackageData();

      const studioPackage = packageData.find(studioPackage => studioPackage.sku === activeSpace.getPackageTypes()[0]);
      const packagePrice = studioPackage && studioPackage.price;

      trackAddToCart({
        altProduct,
        design: activeDesign,
        enabledFilters,
        packagePrice,
        product,
        searchKey,
        searchProducts,
        selectedHotspotProduct,
        studio: activeSpace,
        origin: 'PDP',
        userId
      });
    } catch (error) {
      bugsnagError(error);
    }

    // Stop spinning loader.
    this.setState({ cartIsLoading: false });
  }

  // Links out to retailer ecommerce site.
  buyFromRetailer () {
    const {
      altProduct,
      dispatchGetCurrentUserEmail,
      selectedProduct
    } = this.props;
    const prod = altProduct || selectedProduct;

    modsyMixpanel.trackBuyFromRetailer(
      prod.id(),
      prod.retailer(),
      prod.url(),
      prod.regularPrice(),
      () => {}
    );

    let url = prod.url();
    const timestamp = Date.now();

    if (prod.retailer() === 'Crate & Barrel') {
      const email = dispatchGetCurrentUserEmail() || '';
      const hashedEmail = sha512(email);
      url = `${url}?a=1974&timestamp=${timestamp}&email=${hashedEmail}&prcd=${CRATE_AND_BARREL_PRODUCT_CODE}`;
    }
    // Equivalent of window.open(url) but safer.
    // https://love2dev.com/blog/rel-noopener/
    const newWindow = window.open();
    newWindow.opener = null;
    newWindow.location = url;
  }

  handleQuantityChange = (option) => {
    this.props.updateQuantity(option.value);
  }

  swapAction = () => {
    const {
      altProduct,
      swapInProgress,
      selectedProduct
    } = this.props;

    const prod = altProduct || selectedProduct;

    if (prod && prod.isTooBig() && !swapInProgress) {
      this.setState({ showPreSwapModal: true });
    } else {
      if (this.areMultipleHotspots() && !swapInProgress) {
        this.setState({ showSwapMultipleModal: true });
      } else {
        this.swapOne();
      }
    }
  }

  swapOne = () => {
    const {
      dispatch,
      canLiveSwap,
      getCurrentSelectedHotspotProduct,
      getCurrentSelectedHotspotUid,
      swapInProgress,
      usingPDPHotspotData
    } = this.props;

    if (!canLiveSwap || swapInProgress) { return; }

    // To avoid showing PreSwapModal if swap is cancelled
    this.closePreSwapModal();

    const hotspotProduct = getCurrentSelectedHotspotProduct({ usingPDPHotspotData });
    const hotspotUuid = getCurrentSelectedHotspotUid({ usingPDPHotspotData });
    if (hotspotUuid && hotspotProduct && !getIsSharedSpace()) {
      dispatch(this.swapProductUsingModel(hotspotProduct, [hotspotUuid]));
    }
  }

  swapAll = () => {
    const {
      activeDesign,
      dispatch,
      canLiveSwap,
      getCurrentHotspotsForProduct,
      getCurrentSelectedHotspotProduct,
      selectedHotspotProduct,
      swapInProgress,
      usingPDPHotspotData
    } = this.props;

    if (!canLiveSwap || swapInProgress) { return; }

    // To avoid showing PreSwapModal if swap is cancelled
    this.closePreSwapModal();

    const hotspotProduct = getCurrentSelectedHotspotProduct({ usingPDPHotspotData });
    const hotspotUuids = getCurrentHotspotsForProduct(activeDesign, selectedHotspotProduct.id());
    if (hotspotUuids && hotspotUuids.length > 0 && hotspotProduct && !getIsSharedSpace()) {
      dispatch(this.swapProductUsingModel(hotspotProduct, hotspotUuids));
    }
  }

  areMultipleHotspots = () => {
    const {
      activeDesign,
      getCurrentHotspotsForProduct,
      selectedHotspotProduct
    } = this.props;

    const hotspots = getCurrentHotspotsForProduct(activeDesign, selectedHotspotProduct.id());

    return hotspots.length > 1;
  };

  /**
   * Initiates a product swap event.
   */
  swapProductUsingModel = (hotspotProduct, hotspotUuids) => (dispatch, getState) => {
    const {
      altProduct,
      closePDP,
      dispatchGetCurrentEnabledFiltersFor,
      dispatchGetCurrentSearchKey,
      dispatchGetCurrentSearchResultFor,
      getCurrentSelectedHotspotProduct,
      dispatchGetCurrentUserId,
      dispatchSwapProductBatch,
      selectedProduct,
      trackStudioSwap,
      userIsLoggedIn,
      usingPDPHotspotData
    } = this.props;

    const design = getActiveDesign({ state: getState() });
    const space = getActiveSpace({ state: getState() });
    const activeRender = getActiveRender(getState());

    // Early exit.
    if (!activeRender || !hotspotUuids || hotspotUuids.length <= 0 || !hotspotProduct) {
      return;
    }

    // Analytics.
    const swapInProduct = altProduct || selectedProduct;
    const userId = userIsLoggedIn && dispatchGetCurrentUserId();
    const selectedHotspotProduct = getCurrentSelectedHotspotProduct({ usingPDPHotspotData });
    const searchKey = dispatchGetCurrentSearchKey();
    const searchProducts = dispatchGetCurrentSearchResultFor(searchKey);
    const enabledFilters = dispatchGetCurrentEnabledFiltersFor(searchKey);
    trackStudioSwap({
      altProduct,
      design,
      enabledFilters,
      origin: 'PDP',
      product: swapInProduct,
      searchKey,
      searchProducts,
      selectedHotspotProduct,
      studio: space,
      userId
    });

    const cameraId = activeRender.camera_id;
    setTimeout(() => this.closeSwapMultipleModal(), 1500);
    setTimeout(() => closePDP(), 3000);
    return dispatchSwapProductBatch({
      cameraId,
      design,
      hotspotUuids,
      replaceProduct: hotspotProduct,
      swapInProduct,
      showroomSpace: space.isShowroomSpace(),
      updatedMessengerSwapUIEnabled: true
    });
  }

  closePreSwapModal = () => {
    this.setState({ showPreSwapModal: false });
  }

  closeSwapMultipleModal = () => {
    this.setState({ showSwapMultipleModal: false });
  }

  handlePreSwapSubmit = () => {
    const {
      canLiveSwap,
      swapInProgress
    } = this.props;

    this.closePreSwapModal();

    // If this product is currently selected, then nothing to do.
    // in messenger, swapping while a swap is in progress will get blocked further down
    if (!canLiveSwap || swapInProgress) {
      return null;
    }

    // after modal for too big, check if we need the modal for multiple instances
    if (this.areMultipleHotspots() && !swapInProgress) {
      this.setState({ showSwapMultipleModal: true });
    } else {
      this.swapOne();
    }
  };

  hideSwap = () => {
    const {
      canLiveSwap,
      isShowroomDesign,
      selectedProduct,
      selectedHotspotProduct,
      altProduct
    } = this.props;
    const sharedSpace = getIsSharedSpace();

    let productConsidered = selectedProduct;

    if (altProduct) { productConsidered = altProduct; }

    return !selectedHotspotProduct ||
      sharedSpace ||
      (selectedHotspotProduct &&
      selectedHotspotProduct.id() === productConsidered.id()) ||
      isShowroomDesign ||
      !canLiveSwap ||
      !productConsidered.has3dAssets();
  }

  render () {
    const {
      quantity,
      quantityMultiple,
      swapInProductId,
      swapInProgress,
      replaceInProgress
    } = this.props;
    const {
      cartIsLoading,
      showPreSwapModal,
      showSwapMultipleModal
    } = this.state;
    const {
      buttonText
    } = this.updateCartButton();

    if (showPreSwapModal) {
      return (
        <PreSwapModal
          onToggleModal={this.closePreSwapModal}
          onSwapClick={this.handlePreSwapSubmit}
        />
      );
    }

    if (showSwapMultipleModal) {
      return (
        <SwapMultipleModal
          onToggleModal={this.closeSwapMultipleModal}
          onSwapOneClick={() => this.swapOne()}
          onSwapAllClick={() => this.swapAll()}
          swapInProgress={swapInProgress}
        />
      );
    }

    // We want to reuse all of the complex business logic implmented here
    // for the Monarch refactor to use PDM for UI layer. The single child
    // component here is the MonarchAdapter. So what we want to do here is
    // decorate the MonarchAdapter with the additional props and callbacks
    // it needs from this existing component.
    // If current qty is not a multiple of quantityMultiple,
    // defaults to quantityMultiple.
    const validQuantity = (quantity % quantityMultiple !== 0)
      ? quantityMultiple
      : quantity;

    return React.cloneElement(this.props.children, {
      atcLoading: cartIsLoading,
      atcQuantity: validQuantity,
      isBuyFromRetailer: buttonText === 'Buy from Retailer',
      onAddMainProductToCart: () => this.buyProduct(),
      onBuyFromRetailer: () => this.buyFromRetailer(),
      onQuantityChange: (option) => this.handleQuantityChange(option),
      onSwapClick: () => this.swapAction(),
      swapAllowed: !this.hideSwap(),
      swapInProductId,
      swapInProgress: (swapInProgress || replaceInProgress)
    });
  }
}

const mapStateToProps = (state, router) => {
  const activeDesign = getActiveDesign({ state, router });
  const altProduct = getAlternativeProduct(state);
  const selectedProduct = getSelectedProduct(state);
  let selectedHotspotProduct = getSelectedHotspotProduct(state);
  let selectedHotspot = getSelectedHotspotUid(state);
  let usingPDPHotspotData = false;
  if (!selectedHotspotProduct) {
    selectedHotspotProduct = getPDPHotspotProduct(state);
    selectedHotspot = getPDPHotspotUid(state);
    usingPDPHotspotData = true;
  }
  const fetchingHotspotUuids = SwapService.getFetchingHotspotUuids(state);

  const swapInProgress = (
    // If there is a swap in progress, do not allow
    // starting a second swap.
    SwapService.replacementIsInProgress({state}) ||

    // If the selected hotspot is involved in a swap or fetch,
    // then we do not want to allow swapping. This is needed to prevent
    // instance ID error where User tries to swap a product but that product
    // has already been swapped in the render that is currently in progress.
    (
      selectedHotspot && fetchingHotspotUuids &&
      fetchingHotspotUuids.includes(selectedHotspot)
    )
  );

  const canLiveSwap = (activeDesign && activeDesign.canLiveSwap()) || false;

  const quantityMultiple = getCurrentQuantityMultiple(state);
  const currentQuantity = getCurrentQuantity(state);
  const quantity = (currentQuantity % quantityMultiple !== 0)
    ? quantityMultiple
    : currentQuantity;

  return {
    activeSpace: getActiveSpace({ state, router }),
    activeDesign,
    altProduct,
    canLiveSwap,
    isShowroomDesign: activeDesign && activeDesign.isShowroomDesign(),
    selectedProduct,
    selectedHotspotProduct,
    quantity,
    quantityMultiple,
    replaceInProgress: SwapService.replacementIsInProgress({state}),
    shoppingCart: getShoppingCart(state),
    swapInProductId: SwapService.getReplacingProductId(state),
    swapInProgress,
    usingPDPHotspotData,
    userIsLoggedIn: userIsLoggedIn(state),
    updatedMessengerSwapUIEnabled: getFeatureFlagEnabledBool(FEATURE_FLAGS.UpdatedMessengerSwapUI, state),
    isPhone: getIsPhone(state)
  };
};

const mapDispatchToProps = (dispatch, { activeDesign, onAddToCart, searchOrigin }) => ({
  dispatch,
  dispatchGetCurrentEnabledFiltersFor (name) {
    return dispatch(ProductSearch.getCurrentEnabledFiltersFor(name));
  },
  dispatchGetCurrentPackageData () {
    return dispatch(getCurrentPackageData());
  },
  dispatchGetCurrentSearchResultFor (name) {
    return dispatch(ProductSearch.getCurrentSearchResultFor(name));
  },
  dispatchGetCurrentSearchKey () {
    return dispatch(getCurrentSearchKey());
  },
  dispatchGetCurrentUserEmail () {
    return dispatch(getCurrentUserEmail());
  },
  dispatchGetCurrentUserId () {
    return dispatch(getCurrentUserId());
  },
  dispatchGetHotSpotForProduct ({ design, productId }) {
    return dispatch(getHotspotForProduct({
      design,
      productId
    }));
  },
  dispatchSwapProductBatch (data) {
    return dispatch(SwapService.swapProductBatch(data));
  },
  updateQuantity (quantity) {
    dispatch(updateProductQuantity(quantity));
  },
  trackAddToCart (payload) {
    // Allow using parent to handle analytics events
    if (typeof onAddToCart === 'function') {
      onAddToCart(payload);
      return;
    }

    dispatch(analytics.trackAddToCart(payload));
  },
  addProductToCartCheckout ({ product, productHotspotId, quantity, designId, email }) {
    // analytics
    let whProduct = product.warehouseProduct();
    const atcDesignId = searchOrigin !== 'shop' ? designId : null;
    if (atcDesignId) {
      whProduct.designId = atcDesignId;
    }
    dispatch(userAddedProductFromPDP(email, whProduct));

    // update cart
    return dispatch(addProductToCart({ product, productHotspotId, quantity, designId: atcDesignId }));
  },
  trackStudioSwap (payload) {
    dispatch(analytics.trackStudioSwap(payload));
  },
  getCurrentHotspotsForProduct (design, productId) {
    return dispatch(getAllHotspotsForProduct({design, productId}));
  },
  getCurrentSelectedHotspotProduct ({ usingPDPHotspotData }) {
    if (usingPDPHotspotData) {
      return dispatch(getCurrentPDPHotspotProduct());
    } else {
      return dispatch(getCurrentSelectedHotspotProduct());
    }
  },
  getCurrentSelectedHotspotUid ({ usingPDPHotspotData }) {
    if (usingPDPHotspotData) {
      return dispatch(getCurrentPDPHotspotUid());
    } else {
      return dispatch(getCurrentSelectedHotspotUid());
    }
  }
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withRouter, withConnect)(AddToCartActionsController);
